import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useCreateProductVariants } from '../../hooks';
import { resolveLocalizedText } from '../../lib/localizedText';
import type { ToastSeverity } from '../../lib/useToast';
import type { AttributeDefinition, AttributeOption, ProductVariantAxisValueInput } from '../../model/types';

export interface AxisAttribute {
  attributeCode: string;
  definition: AttributeDefinition;
}

export interface CreateVariantsDialogProps {
  open: boolean;
  onClose: () => void;
  productId: string;
  /** The parent's own code/SKU, seeding the suggested variant-code prefix. */
  parentCode: string;
  axisAttributes: AxisAttribute[];
  onResult: (severity: ToastSeverity, message: string) => void;
}

interface ComboRow {
  key: string;
  axisValues: ProductVariantAxisValueInput[];
  displayLabel: string;
  code: string;
  status: 'idle' | 'success' | 'error';
  message?: string;
}

function slugifySegment(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '');
}

/** Cartesian product of N value lists, preserving axis order. */
function cartesianProduct(lists: string[][]): string[][] {
  return lists.reduce<string[][]>((acc, list) => acc.flatMap((combo) => list.map((v) => [...combo, v])), [[]]);
}

/**
 * "Create variants" dialog (user-story-merchant-portal-031): per-axis-attribute option pickers
 * (SELECT/MULTISELECT axis attributes get a multi-select over the dictionary options; any other
 * axis type falls back to a comma-separated free-text list) generate the cartesian product of
 * combinations, each posted as its own `POST .../variants` call (no batch endpoint — see
 * `useCreateProductVariants`'s fan-out doc). Duplicate-combination `409`s surface per row, not as
 * a single blanket toast, and failed rows can be edited (code) and retried without re-submitting
 * rows that already succeeded.
 */
export function CreateVariantsDialog({
  open,
  onClose,
  productId,
  parentCode,
  axisAttributes,
  onResult,
}: CreateVariantsDialogProps) {
  const { t, i18n } = useTranslation('catalog');
  const createVariants = useCreateProductVariants();

  const [selectedOptions, setSelectedOptions] = useState<Record<string, AttributeOption[]>>({});
  const [freeTextDrafts, setFreeTextDrafts] = useState<Record<string, string>>({});
  const [codeOverrides, setCodeOverrides] = useState<Record<string, string>>({});
  const [submittedRows, setSubmittedRows] = useState<ComboRow[] | null>(null);

  const reset = () => {
    setSelectedOptions({});
    setFreeTextDrafts({});
    setCodeOverrides({});
    setSubmittedRows(null);
  };

  useEffect(() => {
    if (open) reset();
  }, [open]);

  const optionLabel = (attributeCode: string, code: string): string => {
    const definition = axisAttributes.find((a) => a.attributeCode === attributeCode)?.definition;
    const option = definition?.options?.find((o) => o.code === code);
    return option ? resolveLocalizedText(option.translations, 'label', i18n.language, code) : code;
  };

  const axisLabel = (attributeCode: string): string => {
    const definition = axisAttributes.find((a) => a.attributeCode === attributeCode)?.definition;
    return definition
      ? resolveLocalizedText(definition.translations, 'label', i18n.language, definition.code)
      : attributeCode;
  };

  const valuesByAxis: string[][] = axisAttributes.map((a) => {
    if ((a.definition.options ?? []).length > 0) {
      return (selectedOptions[a.attributeCode] ?? []).map((o) => o.code);
    }
    return (freeTextDrafts[a.attributeCode] ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  });
  const everyAxisHasValues = axisAttributes.length > 0 && valuesByAxis.every((list) => list.length > 0);

  const derivedRows: ComboRow[] = useMemo(() => {
    if (!everyAxisHasValues) return [];
    const combos = cartesianProduct(valuesByAxis);
    return combos.map((combo) => {
      const key = combo.join('|');
      const axisValues: ProductVariantAxisValueInput[] = axisAttributes.map((a, i) => ({
        attributeCode: a.attributeCode,
        value: combo[i],
      }));
      const displayLabel = combo.map((v, i) => optionLabel(axisAttributes[i].attributeCode, v)).join(' / ');
      const defaultCode = [parentCode, ...combo.map(slugifySegment)].filter(Boolean).join('-');
      return {
        key,
        axisValues,
        displayLabel,
        code: codeOverrides[key] ?? defaultCode,
        status: 'idle',
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [everyAxisHasValues, JSON.stringify(valuesByAxis), codeOverrides, parentCode]);

  const rows = submittedRows ?? derivedRows;
  const editableRows = submittedRows ? submittedRows.filter((r) => r.status !== 'success') : rows;

  const codes = editableRows.map((r) => r.code.trim());
  const hasEmptyCode = codes.some((c) => c === '');
  const hasDuplicateCode = new Set(codes).size !== codes.length && codes.length > 0;
  const canSubmit = editableRows.length > 0 && !hasEmptyCode && !hasDuplicateCode && !createVariants.isPending;

  const updateRowCode = (key: string, code: string) => {
    if (submittedRows) {
      setSubmittedRows((current) => current?.map((r) => (r.key === key ? { ...r, code } : r)) ?? null);
    } else {
      setCodeOverrides((current) => ({ ...current, [key]: code }));
    }
  };

  const handleSubmit = () => {
    const toSubmit = editableRows;
    // Captured once and passed by reference to the mutation — `failed[].request` in the result is
    // the SAME object reference (see `useCreateProductVariants`'s `requests[index]`), so matching
    // a row back to its outcome is a reliable `===` lookup, not a fragile value comparison.
    const requestPayload = toSubmit.map((r) => ({ code: r.code.trim(), axisValues: r.axisValues }));

    createVariants.mutate(
      { productId, requests: requestPayload },
      {
        onSuccess: (result) => {
          const nextRows: ComboRow[] = toSubmit.map((row, index) => {
            const failure = result.failed.find((f) => f.request === requestPayload[index]);
            return failure ? { ...row, status: 'error', message: failure.message } : { ...row, status: 'success' };
          });
          const alreadySucceeded = submittedRows?.filter((r) => r.status === 'success') ?? [];
          const allRows = [...alreadySucceeded, ...nextRows];
          setSubmittedRows(allRows);

          if (result.failed.length === 0) {
            const totalSucceeded = allRows.filter((r) => r.status === 'success').length;
            onResult('success', t('createVariantsDialog.allCreated', { count: totalSucceeded }));
            handleClose();
          } else if (result.succeeded.length === 0) {
            onResult('error', t('createVariantsDialog.allFailed', { message: result.failed[0].message }));
          } else {
            onResult(
              'warning',
              t('createVariantsDialog.partialCreated', {
                succeededCount: result.succeeded.length,
                totalCount: toSubmit.length,
                message: result.failed[0].message,
              }),
            );
          }
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t('createVariantsDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <DialogContentText>{t('createVariantsDialog.description')}</DialogContentText>

          {!submittedRows ? (
            <Stack spacing={2}>
              {axisAttributes.map((a) => {
                const options = a.definition.options ?? [];
                if (options.length > 0) {
                  return (
                    <Autocomplete
                      key={a.attributeCode}
                      multiple
                      options={options.filter((o) => o.active)}
                      value={selectedOptions[a.attributeCode] ?? []}
                      onChange={(_e, value) =>
                        setSelectedOptions((current) => ({ ...current, [a.attributeCode]: value }))
                      }
                      getOptionLabel={(option) => resolveLocalizedText(option.translations, 'label', i18n.language, option.code)}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      renderInput={(params) => <TextField {...params} label={axisLabel(a.attributeCode)} size="small" />}
                    />
                  );
                }
                return (
                  <TextField
                    key={a.attributeCode}
                    label={axisLabel(a.attributeCode)}
                    value={freeTextDrafts[a.attributeCode] ?? ''}
                    onChange={(e) =>
                      setFreeTextDrafts((current) => ({ ...current, [a.attributeCode]: e.target.value }))
                    }
                    helperText={t('createVariantsDialog.commaSeparatedHint')}
                    size="small"
                    fullWidth
                  />
                );
              })}
            </Stack>
          ) : null}

          <Divider />

          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('createVariantsDialog.noCombinationsYet')}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">
                {t('createVariantsDialog.combinationsCount', { count: rows.length })}
              </Typography>
              {rows.map((row) => (
                <Stack
                  key={row.key}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ sm: 'center' }}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Chip size="small" variant="outlined" label={row.displayLabel} />
                    {row.status === 'error' ? (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }} role="alert">
                        {row.message}
                      </Typography>
                    ) : null}
                  </Box>
                  <TextField
                    label={t('createVariantsDialog.codeLabel')}
                    value={row.code}
                    onChange={(e) => updateRowCode(row.key, e.target.value)}
                    size="small"
                    disabled={row.status === 'success'}
                    error={row.code.trim() === ''}
                    sx={{ minWidth: 200 }}
                    slotProps={{ htmlInput: { style: { fontFamily: 'JetBrains Mono, monospace' } } }}
                  />
                  {row.status === 'success' ? (
                    <CheckCircleRoundedIcon color="success" fontSize="small" />
                  ) : row.status === 'error' ? (
                    <ErrorRoundedIcon color="error" fontSize="small" />
                  ) : null}
                </Stack>
              ))}
              {hasDuplicateCode ? <Alert severity="warning">{t('createVariantsDialog.duplicateCodeWarning')}</Alert> : null}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createVariants.isPending}>
          {t('common:action.close')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={createVariants.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submittedRows
            ? t('createVariantsDialog.retryButton', { count: editableRows.length })
            : t('createVariantsDialog.createButton', { count: rows.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
